/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import { useAgency } from '@/pages/authenticated/agency-pages/AgenciesContext';
import { useClientOpenAttendance } from './useClientOpenAttendance';

type Member = { email?: string; first_name?: string; last_name?: string };

export interface ClientResponsible {
  /**
   * The broker the client's record names as responsible
   * (`assignment.primary_broker`), or null when nothing does.
   */
  ownerName: string | null;
  /** Display name of the broker running the current attendance, if any. */
  attendingName: string | null;
  /** The attendance `attendingName` was read off, for the "open it" action. */
  dealId: string | null;
  /** How many attendances are open besides that one. */
  otherOpenCount: number;
  /** Whether both names are the same person — then one row says it all. */
  samePerson: boolean;
}

/**
 * Resolves a broker e-mail to the member's display name.
 *
 * @param email - The e-mail stored on the record or the attendance.
 * @param members - The agency's members.
 * @returns The member's full name, their e-mail, or null when blank.
 */
function displayName(email: string | null | undefined, members: Member[]): string | null {
  const key = (email ?? '').toLowerCase();
  if (!key) return null;
  const member = members.find((m) => (m.email ?? '').toLowerCase() === key);
  return member ? memberName(member, email as string) : (email as string);
}

/**
 * A member's full name, falling back to their e-mail.
 *
 * @param member - The agency member.
 * @param fallback - What to answer when the member has neither.
 * @returns The display name.
 */
function memberName(member: Member, fallback: string): string {
  const full = [member.first_name, member.last_name].filter(Boolean).join(' ').trim();
  return full || member.email || fallback;
}

/**
 * The two brokers a client can have beside them.
 *
 * The record names an owner — `assignment.primary_broker`, the same field
 * a listing carries — and that answer is stable. The attendance names
 * whoever is handling them right now, which comes and goes with the deal.
 * They are frequently the same person and frequently not, so both are
 * returned rather than one standing in for the other.
 *
 * @param agencyId - The owning agency.
 * @param clientId - The client being shown.
 * @param primaryBroker - `assignment.primary_broker` off the client record.
 * @returns Both names, the attendance to open, and how many others are open.
 */
export function useClientResponsible(
  agencyId: string,
  clientId: string,
  primaryBroker?: string,
): ClientResponsible {
  const { openAttendances } = useClientOpenAttendance(agencyId, clientId);
  const members = useAgency((state) => state.agency?.members);

  return useMemo(() => {
    const list = (members ?? []) as Member[];
    const current = openAttendances[0];
    const ownerName = displayName(primaryBroker, list);
    const attendingName = current ? displayName(current.broker_id, list) : null;
    const samePerson = Boolean(
      ownerName && attendingName
      && (primaryBroker ?? '').toLowerCase() === (current?.broker_id ?? '').toLowerCase(),
    );
    return {
      ownerName,
      attendingName,
      dealId: current?.deal_id ?? null,
      otherOpenCount: Math.max(openAttendances.length - 1, 0),
      samePerson,
    };
  }, [openAttendances, members, primaryBroker]);
}

/**
 * The display name of one agency member, by e-mail.
 *
 * Used for `assignment.captured_by`: who brought the client in, a fact
 * about the record that no attendance changes.
 *
 * @param email - The member's e-mail, if any.
 * @returns The member's full name, the e-mail when nobody matches, or null.
 */
export function useAgencyMemberName(email?: string): string | null {
  const members = useAgency((state) => state.agency?.members);
  return useMemo(() => displayName(email, (members ?? []) as Member[]), [email, members]);
}
